import React, { useState, useEffect, useMemo } from 'react';
import { FaSitemap, FaSearch, FaCheck, FaTimes } from 'react-icons/fa';
import { Card, SectionTitle, CountPill, Spinner, EmptyState } from './Primitives';

/**
 * Step 3: selezione multipla degli scheletri Milo, organizzati per root.
 * La selezione è cross-root: ogni scheletro è identificato da { root, path }.
 */
export default function SkeletonSelector({
  skeletonRoots,
  skeletonsByRoot,
  onLoadRoot,
  selectedSkeletons,
  isSkeletonSelected,
  onToggleSkeleton,
  onClear,
}) {
  const [activeRoot, setActiveRoot] = useState('');
  const [query, setQuery] = useState('');

  // Seleziona la prima root disponibile e caricala.
  useEffect(() => {
    if (!activeRoot && skeletonRoots.length > 0) {
      setActiveRoot(skeletonRoots[0]);
    }
  }, [skeletonRoots, activeRoot]);

  useEffect(() => {
    if (activeRoot) onLoadRoot(activeRoot);
  }, [activeRoot, onLoadRoot]);

  const currentList = skeletonsByRoot[activeRoot];
  const loading = activeRoot && currentList === undefined;

  const filtered = useMemo(() => {
    const list = currentList || [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((s) =>
      String(s.label || s.path || '').toLowerCase().includes(q),
    );
  }, [currentList, query]);

  return (
    <Card className="p-5">
      <SectionTitle
        icon={FaSitemap}
        title="Scheletri Milo"
        subtitle="Seleziona uno o più scheletri da applicare"
        action={
          <div className="flex items-center gap-2">
            {selectedSkeletons.length > 0 && (
              <CountPill>{selectedSkeletons.length} selezionati</CountPill>
            )}
            <button
              type="button"
              onClick={onClear}
              disabled={selectedSkeletons.length === 0}
              className="rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary dark:hover:bg-gray-700 disabled:opacity-40"
            >
              Azzera
            </button>
          </div>
        }
      />

      {/* Chip degli scheletri selezionati (anche da altre root) */}
      {selectedSkeletons.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {selectedSkeletons.map((s) => (
            <span
              key={`${s.root}::${s.path}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 py-1 pl-3 pr-1.5 text-xs font-medium text-primary dark:bg-primary/20"
            >
              <span className="max-w-[16rem] truncate" title={`${s.root} / ${s.path}`}>
                {s.path.split('/').pop()}
              </span>
              <button
                type="button"
                onClick={() => onToggleSkeleton(s)}
                aria-label={`Rimuovi ${s.path}`}
                className="rounded-full p-0.5 hover:bg-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <FaTimes size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Tab delle root */}
      <div className="mt-4 flex flex-wrap gap-1.5" role="tablist" aria-label="Radici scheletri">
        {skeletonRoots.map((root) => (
          <button
            key={root}
            type="button"
            role="tab"
            aria-selected={activeRoot === root}
            onClick={() => setActiveRoot(root)}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              activeRoot === root
                ? 'bg-primary text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            {root}
          </button>
        ))}
      </div>

      <div className="relative mt-3">
        <FaSearch
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          size={12}
        />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filtra scheletri…"
          aria-label="Filtra scheletri"
          className="w-full rounded-xl border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />
      </div>

      <div className="mt-3">
        {loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-gray-500 dark:text-gray-400">
            <Spinner className="h-4 w-4" /> Caricamento scheletri…
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={FaSitemap}
            title="Nessuno scheletro"
            hint="Cambia root o rimuovi il filtro testuale."
          />
        ) : (
          <ul className="grid max-h-72 grid-cols-1 gap-1.5 overflow-y-auto pr-1">
            {filtered.map((s) => {
              const skeleton = { root: activeRoot, path: s.path };
              const selected = isSkeletonSelected(skeleton);
              return (
                <li key={s.path}>
                  <button
                    type="button"
                    onClick={() => onToggleSkeleton(skeleton)}
                    aria-pressed={selected}
                    className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left text-sm transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                      selected
                        ? 'border-primary bg-primary/5 dark:bg-primary/10'
                        : 'border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        selected
                          ? 'border-primary bg-primary text-white'
                          : 'border-gray-300 dark:border-gray-500'
                      }`}
                      aria-hidden="true"
                    >
                      {selected && <FaCheck size={9} />}
                    </span>
                    <span className="truncate text-gray-700 dark:text-gray-200" title={s.label || s.path}>
                      {s.label || s.path}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}
